import { observer } from 'mobx-react-lite'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import Styles from '../../BlogMetadata.module.scss'
import { BlogMetadataCompositionReturn } from '../../hooks/useBlogMetadataComposition'
import {
  BlogMetadataHeader,
  BlogMetadataImageSection,
  BlogMetadataCategoriesSection,
  ShopifyIntegrationFields,
  SummaryField,
  TitleField,
  SlugField,
} from '..'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

/**
 * Shopify-specific layout with custom field order
 * Order: Header → Blog → Author → Title → Summary → Image → Tags → Slug
 * Note: Shopify doesn't support categories, so only tags are shown
 */
export const ShopifyBlogMetadataLayout = observer(
  ({ integrationInfo, states, handlers, refs }: BlogMetadataCompositionReturn) => {
    const isSystemRedesignEnabled = useIsSystemRedesign()

    return (
      <div className={Styles.container} data-testid="blog-metadata-v2">
        <VisuallyHidden>
          <input
            aria-label="Upload featured image"
            ref={refs.fileInputRef}
            type="file"
            multiple={false}
            onChange={handlers.imageHandlers.handleFileInputChange}
            accept="image/*"
          />
        </VisuallyHidden>

        <BlogMetadataHeader blogIntegrationsCount={integrationInfo.blogIntegrationsCount} />

        {/* Shopify Blog & Author */}
        <ShopifyIntegrationFields
          integrationState={states.integrationState}
          handlers={handlers.enhancedIntegrationHandlers}
          isPostPublished={integrationInfo.isPostPublished}
        />

        {/* Title */}
        <TitleField
          value={states.formState.title}
          onChange={handlers.enhancedFormHandlers.handleTitleChange}
          size={isSystemRedesignEnabled ? 'md' : 'lg'}
        />

        {/* Summary (Excerpt) */}
        <SummaryField
          label="Summary"
          value={states.formState.summary}
          onChange={handlers.enhancedFormHandlers.handleSummaryChange}
        />

        {/* Featured Image & Alt Text */}
        <BlogMetadataImageSection imagesState={states.imageState} handlers={handlers.imageHandlers} />

        <BlogMetadataCategoriesSection
          categoriesState={states.categoriesState}
          categoriesHandlers={handlers.enhancedCategoriesHandlers}
          tagsState={states.tagsState}
          tagsHandlers={handlers.enhancedTagsHandlers}
          useCategoryDropdown={states.integrationState.useCategoryDropdown}
          hideCategories={true}
        />

        {/* Slug */}
        <SlugField
          value={states.formState.slug}
          onChange={handlers.enhancedFormHandlers.handleSlugChange}
          size={isSystemRedesignEnabled ? 'md' : 'lg'}
        />
      </div>
    )
  },
)
